import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Flame, X, ArrowRight, Loader2 } from "lucide-react";
import FoundingShareCard from "@/components/FoundingShareCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SUPPRESS_KEY = "hpm_courtroom_exit_intent_dismissed_v1";

/**
 * Exit-intent capture for /courtroom.
 *
 * Fires ONCE per browser when the visitor's mouse exits the viewport from the
 * top edge — the classic "user is about to bounce" tell. Captures their email
 * straight into the existing waitlist with source="exit_intent_modal" so you
 * can attribute conversions in /hpm-admin.
 *
 * No-op (a) on touch devices, (b) when the visitor already converted, or
 * (c) when the modal was already shown/dismissed on this browser.
 */
export default function ExitIntentModal({ alreadyConverted = false }) {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [result, setResult] = useState(null);
    const firedRef = useRef(false);

    useEffect(() => {
        if (alreadyConverted) return;
        try {
            if (localStorage.getItem(SUPPRESS_KEY) === "1") return;
        } catch {
            // localStorage blocked — still allow the modal to fire once per session.
        }
        // Skip touch devices: mouseleave from top is unreliable / annoying on mobile.
        if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches) {
            return;
        }

        const handler = (e) => {
            if (firedRef.current) return;
            // Only fire if cursor exits through the TOP edge (toward tab bar / close)
            if (e.clientY > 4) return;
            // Ignore if user has spent less than 4s on page (probably misclick reload)
            if (performance.now() < 4000) return;
            firedRef.current = true;
            setOpen(true);
        };

        document.addEventListener("mouseout", handler);
        return () => document.removeEventListener("mouseout", handler);
    }, [alreadyConverted]);

    const dismiss = () => {
        setOpen(false);
        try {
            localStorage.setItem(SUPPRESS_KEY, "1");
        } catch {
            /* ignore */
        }
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!email.includes("@")) {
            toast.error("Enter a valid email");
            return;
        }
        setBusy(true);
        try {
            let refBy;
            try {
                refBy = parseInt(sessionStorage.getItem("hpm_courtroom_ref") || "", 10);
                if (!Number.isFinite(refBy) || refBy <= 0) refBy = undefined;
            } catch {
                refBy = undefined;
            }
            const r = await axios.post(`${API}/courtroom/waitlist`, {
                email,
                interest: "waitlist",
                source: "exit_intent_modal",
                referred_by: refBy,
            });
            setResult(r.data || null);
            setDone(true);
            try {
                localStorage.setItem(SUPPRESS_KEY, "1");
            } catch {
                /* ignore */
            }
            toast.success("You're on the founding list.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Submission failed");
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="exit-intent-modal"
            onClick={dismiss}
        >
            <div
                className="relative w-full max-w-lg bg-black border-2 border-[#FF3B30] p-8 sm:p-10"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={dismiss}
                    className="absolute top-3 right-3 text-white/40 hover:text-white transition-colors"
                    aria-label="Close"
                    data-testid="exit-intent-close"
                >
                    <X size={18} />
                </button>

                {done ? (
                    <div data-testid="exit-intent-success">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF85] mb-3">
                            ✓ FOUNDING LIST · CONFIRMED
                        </div>
                        <div className="font-anton text-3xl uppercase leading-none mb-4">
                            You'll hear from me directly.
                        </div>
                        <p className="font-sans text-sm text-white/70 leading-relaxed">
                            No drip sequences. No noise. When the doors open, you get the email — and
                            your founding-member spot at $99 lifetime.
                        </p>
                        <FoundingShareCard
                            spotNumber={result?.spot_number}
                            foundingTotal={result?.founding_total || 100}
                        />
                        <button
                            onClick={dismiss}
                            className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 hover:text-white transition-colors"
                            data-testid="exit-intent-dismiss-done"
                        >
                            CLOSE →
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-3 flex items-center gap-2">
                            <Flame size={11} /> A NOTE FROM THE FOUNDER
                        </div>
                        <div className="font-anton text-3xl sm:text-4xl uppercase leading-[0.95] mb-5">
                            Wait. Before you go.
                        </div>
                        <p className="font-sans text-sm text-white/80 leading-relaxed mb-3">
                            I'm building HPM Courtroom in public — out of frustration that the only
                            way young lawyers learn to actually <em className="text-white">try cases</em>{" "}
                            is to lose their first few and pray nobody important is watching.
                        </p>
                        <p className="font-sans text-sm text-white/80 leading-relaxed mb-5">
                            If that resonates, drop your email. No spam, no drip. One message when
                            we open the doors — and your founding-member spot held at $99 lifetime.
                        </p>
                        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
                            <input
                                required
                                type="email"
                                placeholder="WORK EMAIL"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="flex-1 bg-black border-2 border-white/30 p-3 font-mono text-xs text-white placeholder-white/40 focus:border-[#FF3B30] outline-none"
                                data-testid="exit-intent-email"
                            />
                            <button
                                type="submit"
                                disabled={busy}
                                className="btn-brutal btn-brutal-red justify-center text-[10px] shrink-0"
                                data-testid="exit-intent-submit"
                            >
                                {busy ? (
                                    <Loader2 size={12} className="animate-spin" />
                                ) : (
                                    <>
                                        HOLD MY SPOT <ArrowRight size={12} />
                                    </>
                                )}
                            </button>
                        </form>
                        <button
                            onClick={dismiss}
                            className="mt-4 font-mono text-[9px] uppercase tracking-[0.3em] text-white/40 hover:text-white/70 transition-colors"
                            data-testid="exit-intent-no-thanks"
                        >
                            NO THANKS, I'LL PASS →
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
